import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/ui/Sidebar';
import UserAccountMenu from '../../components/ui/UserAccountMenu';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import CalendarGrid from './components/CalendarGrid';
import AppointmentDetailModal from './components/AppointmentDetailModal';
import RescheduleModal from './components/RescheduleModal';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

const MarketingSupportCalendar = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const [view, setView] = useState('month');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [appointments, setAppointments] = useState([]);
  const [filteredAppointments, setFilteredAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const isAdmin = userProfile?.role === 'Administrator' || userProfile?.role === 'admin';

  // Fetch appointments from database
  useEffect(() => {
    fetchAppointments();
  }, [user]);

  const fetchAppointments = async () => {
    try {
      setLoading(true);
      let query = supabase
        ?.from('marketing_support_bookings')
        ?.select('*')
        ?.order('appointment_date', { ascending: true });

      // If not admin, filter by user's email
      if (!isAdmin && user?.email) {
        query = query?.eq('email', user?.email);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching appointments:', error);
      } else {
        setAppointments(data || []);
        setFilteredAppointments(data || []);
      }
    } catch (err) {
      console.error('Failed to fetch appointments:', err);
    } finally {
      setLoading(false);
    }
  };

  // Apply filters
  useEffect(() => {
    let filtered = [...appointments];

    // Filter by type
    if (filterType !== 'all') {
      filtered = filtered?.filter(apt => apt?.support_type === filterType);
    }

    // Filter by search query
    if (searchQuery?.trim()) {
      let query = searchQuery?.toLowerCase();
      filtered = filtered?.filter(apt => 
        apt?.full_name?.toLowerCase()?.includes(query) ||
        apt?.email?.toLowerCase()?.includes(query) ||
        apt?.practice_name?.toLowerCase()?.includes(query)
      );
    }

    setFilteredAppointments(filtered);
  }, [filterType, searchQuery, appointments]);

  const handlePreviousMonth = () => {
    const newDate = new Date(currentDate);
    newDate?.setMonth(currentDate?.getMonth() - 1);
    setCurrentDate(newDate);
  };

  const handleNextMonth = () => {
    const newDate = new Date(currentDate);
    newDate?.setMonth(currentDate?.getMonth() + 1);
    setCurrentDate(newDate);
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const handleAppointmentClick = (appointment) => {
    setSelectedAppointment(appointment);
    setShowDetailModal(true);
  };

  const handleReschedule = (appointment) => {
    setSelectedAppointment(appointment);
    setShowDetailModal(false);
    setShowRescheduleModal(true);
  };

  const handleCancelAppointment = async (appointmentId) => {
    if (!window.confirm('Are you sure you want to cancel this appointment?')) {
      return;
    }

    try {
      // Get appointment details before deletion
      const appointmentToCancel = appointments?.find(apt => apt?.id === appointmentId);
      
      const { error } = await supabase
        ?.from('marketing_support_bookings')
        ?.delete()
        ?.eq('id', appointmentId);

      if (error) {
        console.error('Error canceling appointment:', error);
        alert('Failed to cancel appointment. Please try again.');
      } else {
        // Send cancellation email
        try {
          await supabase?.functions?.invoke('send-booking-email', {
            body: {
              action: 'cancel',
              appointmentData: appointmentToCancel
            }
          });
        } catch (emailError) {
          console.error('Failed to send cancellation email:', emailError);
          // Don't block the cancellation if email fails
        }
        
        alert('Appointment canceled successfully.');
        fetchAppointments();
        setShowDetailModal(false);
      }
    } catch (err) {
      console.error('Failed to cancel appointment:', err);
      alert('An error occurred. Please try again.');
    }
  };

  const handleRescheduleComplete = async (oldAppointment, newSlot) => {
    // Send reschedule email
    try {
      await supabase?.functions?.invoke('send-booking-email', {
        body: {
          action: 'reschedule',
          appointmentData: oldAppointment,
          newSlot: newSlot
        }
      });
    } catch (emailError) {
      console.error('Failed to send reschedule email:', emailError);
      // Don't block the reschedule if email fails
    }
    
    setShowRescheduleModal(false);
    setSelectedAppointment(null);
    fetchAppointments();
  };

  const supportTypes = [
    { value: 'all', label: 'All Types' },
    { value: 'strategy', label: 'Strategy Consultation' },
    { value: 'content', label: 'Content Planning' },
    { value: 'technical', label: 'Technical Support' },
    { value: 'training', label: 'Training Session' }
  ];

  const monthYear = currentDate?.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  return (
    <div className="min-h-screen bg-background flex">
      <Sidebar />
      <div className="flex-1 lg:ml-60">
        <header className="sticky top-0 z-100 bg-white border-b border-border h-20 flex items-center justify-between px-4 md:px-6 lg:px-8">
          <div>
            <h1 className="text-xl md:text-2xl font-light uppercase text-foreground">
              Marketing Support Calendar
            </h1>
            <p className="text-sm text-muted-foreground font-caption mt-1">
              {isAdmin ? 'View and manage all appointments' : 'View and manage your appointments'}
            </p>
          </div>
          <UserAccountMenu />
        </header>

        <main className="p-4 md:p-6 lg:p-8">
          {/* Controls Bar */}
          <div className="mb-6 flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            {/* Calendar Navigation */}
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePreviousMonth}
                iconName="ChevronLeft"
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={handleToday}
              >
                Today
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleNextMonth}
                iconName="ChevronRight"
                iconPosition="right"
              />
              <h2 className="text-lg font-semibold text-foreground ml-2">{monthYear}</h2>
            </div>

            {/* View Toggle */}
            <div className="flex items-center gap-2">
              <Button
                variant={view === 'month' ? 'primary' : 'outline'}
                size="sm"
                onClick={() => setView('month')}
              >
                Month
              </Button>
              <Button
                variant={view === 'week' ? 'primary' : 'outline'}
                size="sm"
                onClick={() => setView('week')}
              >
                Week
              </Button>
            </div>
          </div>

          {/* Filters */}
          <div className="mb-6 flex flex-col md:flex-row gap-4">
            {/* Type Filter */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-foreground mb-2">
                Filter by Type
              </label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e?.target?.value)}
                className="w-full h-10 px-3 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {supportTypes?.map(type => (
                  <option key={type?.value} value={type?.value}>
                    {type?.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Search */}
            {isAdmin && (
              <div className="flex-1">
                <label className="block text-sm font-medium text-foreground mb-2">
                  Search Appointments
                </label>
                <div className="relative">
                  <Icon
                    name="Search"
                    size={18}
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
                  />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e?.target?.value)}
                    placeholder="Search by name, email, or practice..."
                    className="w-full h-10 pl-10 pr-3 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>
            )}

            {/* Stats */}
            <div className="flex items-end gap-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-foreground">{filteredAppointments?.length}</p>
                <p className="text-xs text-muted-foreground">Total</p>
              </div>
            </div>
          </div>

          {/* Calendar Grid */}
          {loading ? (
            <div className="flex items-center justify-center h-96">
              <div className="text-center">
                <Icon name="Loader" size={40} className="animate-spin text-primary mx-auto mb-4" />
                <p className="text-muted-foreground">Loading appointments...</p>
              </div>
            </div>
          ) : (
            <CalendarGrid
              view={view}
              currentDate={currentDate}
              appointments={filteredAppointments}
              onAppointmentClick={handleAppointmentClick}
            />
          )}

          {/* Quick Actions */}
          <div className="mt-6 flex justify-end">
            <Button
              variant="primary"
              onClick={() => navigate('/schedule-marketing-support')}
              iconName="Plus"
            >
              Schedule New Appointment
            </Button>
          </div>
        </main>
      </div>

      {/* Appointment Detail Modal */}
      {showDetailModal && selectedAppointment && (
        <AppointmentDetailModal
          appointment={selectedAppointment}
          isAdmin={isAdmin}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedAppointment(null);
          }}
          onReschedule={handleReschedule}
          onCancel={handleCancelAppointment}
        />
      )}

      {/* Reschedule Modal */}
      {showRescheduleModal && selectedAppointment && (
        <RescheduleModal
          appointment={selectedAppointment}
          onClose={() => {
            setShowRescheduleModal(false);
            setSelectedAppointment(null);
          }}
          onComplete={handleRescheduleComplete}
        />
      )}
    </div>
  );
};

export default MarketingSupportCalendar;